import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { FileText, Download, Eye, TrendingUp, DollarSign, Calendar, Building } from 'lucide-react';
import { BACKEND_URL } from '../utils/config';

const API = `${BACKEND_URL}/api`;

const Reports = () => {
  const [reports, setReports] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  
  // Filters
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedType, setSelectedType] = useState('annual');
  const [selectedMonth, setSelectedMonth] = useState(null);
  const [selectedQuarter, setSelectedQuarter] = useState(null);
  const [selectedRole, setSelectedRole] = useState('admin');
  
  // Dialog states
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [selectedReport, setSelectedReport] = useState(null);
  const [showReportDetails, setShowReportDetails] = useState(false);

  useEffect(() => {
    fetchCompanies();
    fetchReports();
  }, []);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data);
      if (response.data.length > 0) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/reports`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReports(response.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateReport = async () => {
    if (!selectedCompany) {
      alert('Seleziona un\'azienda');
      return;
    }

    setGenerating(true);
    try {
      const token = localStorage.getItem('hw_token');
      const requestData = {
        company_id: selectedCompany,
        report_type: selectedType,
        report_role: selectedRole,
        year: selectedYear
      };

      if (selectedType === 'monthly' && selectedMonth) {
        requestData.month = parseInt(selectedMonth);
      } else if (selectedType === 'quarterly' && selectedQuarter) {
        requestData.quarter = parseInt(selectedQuarter);
      }

      const response = await axios.post(
        `${API}/reports/generate`,
        requestData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      alert('Report generato con successo!');
      setShowGenerateDialog(false);
      fetchReports();
    } catch (error) {
      console.error('Error generating report:', error);
      alert('Errore nella generazione del report: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGenerating(false);
    }
  };

  const downloadReport = async (reportId) => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(
        `${API}/reports/${reportId}/download`,
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `report_${reportId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error downloading report:', error);
      alert('Errore nel download del report');
    }
  };

  const viewReportDetails = async (reportId) => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(
        `${API}/reports/${reportId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedReport(response.data);
      setShowReportDetails(true);
    } catch (error) {
      console.error('Error fetching report details:', error);
      alert('Errore nel caricamento dei dettagli');
    }
  };

  const getReportTypeLabel = (type) => {
    const labels = {
      monthly: 'Mensile',
      quarterly: 'Trimestrale',
      annual: 'Annuale'
    };
    return labels[type] || type;
  };

  const getReportPeriod = (report) => {
    if (report.report_type === 'monthly' && report.month) {
      return `Mese ${report.month}/${report.year}`;
    } else if (report.report_type === 'quarterly' && report.quarter) {
      return `Q${report.quarter} ${report.year}`;
    } else {
      return `Anno ${report.year}`;
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
              <FileText className="h-8 w-8 text-blue-600" />
              Report Finanziari
            </h1>
            <p className="text-gray-600 mt-1">
              Genera e visualizza report con analisi AI avanzata
            </p>
          </div>
          
          <Dialog open={showGenerateDialog} onOpenChange={setShowGenerateDialog}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white">
                <TrendingUp className="h-4 w-4 mr-2" />
                Genera Nuovo Report
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Genera Nuovo Report</DialogTitle>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Azienda</label>
                  <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                    <SelectTrigger>
                      <SelectValue placeholder="Seleziona azienda" />
                    </SelectTrigger>
                    <SelectContent>
                      {companies.map(company => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Tipo Report</label>
                  <Select value={selectedType} onValueChange={setSelectedType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Mensile</SelectItem>
                      <SelectItem value="quarterly">Trimestrale</SelectItem>
                      <SelectItem value="annual">Annuale</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Destinatario Report</label>
                  <Select value={selectedRole} onValueChange={setSelectedRole}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="admin">Admin - Report Completo</SelectItem>
                      <SelectItem value="bookings_operator">Operatore Prenotazioni</SelectItem>
                      <SelectItem value="customer_service">Customer Service</SelectItem>
                      <SelectItem value="invoice_operator">Operatore Fatture</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <label className="text-sm font-medium mb-2 block">Anno</label>
                  <Select value={selectedYear.toString()} onValueChange={(val) => setSelectedYear(parseInt(val))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026].map(year => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {selectedType === 'monthly' && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Mese</label>
                    <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona mese" />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(12)].map((_, i) => (
                          <SelectItem key={i + 1} value={(i + 1).toString()}>
                            Mese {i + 1}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                {selectedType === 'quarterly' && (
                  <div>
                    <label className="text-sm font-medium mb-2 block">Trimestre</label>
                    <Select value={selectedQuarter} onValueChange={setSelectedQuarter}>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleziona trimestre" />
                      </SelectTrigger>
                      <SelectContent>
                        {[1, 2, 3, 4].map(q => (
                          <SelectItem key={q} value={q.toString()}>
                            Q{q}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                
                <Button
                  onClick={generateReport}
                  disabled={generating}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  {generating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Generazione in corso...
                    </>
                  ) : (
                    'Genera Report'
                  )}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Reports List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Caricamento report...</p>
          </div>
        ) : reports.length === 0 ? (
          <Card className="p-12 text-center">
            <FileText className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-700 mb-2">
              Nessun Report Disponibile
            </h3>
            <p className="text-gray-600 mb-4">
              Genera il tuo primo report per iniziare l'analisi finanziaria
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {reports.map(report => {
              const companyName = companies.find(c => c.id === report.company_id)?.name || 'Unknown';
              
              return (
                <Card key={report.id} className="p-5 hover:shadow-lg transition-shadow">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <Badge className="mb-2">{getReportTypeLabel(report.report_type)}</Badge>
                      <h3 className="font-bold text-lg text-gray-800">{companyName}</h3>
                      <p className="text-sm text-gray-600 flex items-center gap-1 mt-1">
                        <Calendar className="h-3 w-3" />
                        {getReportPeriod(report)}
                      </p>
                    </div>
                    <FileText className="h-8 w-8 text-blue-600" />
                  </div>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Ricavi:</span>
                      <span className="font-semibold text-green-700">
                        {formatCurrency(report.total_revenue)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">EBITDA:</span>
                      <span className={`font-semibold ${report.ebitda >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(report.ebitda)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Margine:</span>
                      <span className={`font-semibold ${report.total_margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(report.total_margin)}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      onClick={() => viewReportDetails(report.id)}
                      variant="outline"
                      size="sm"
                      className="flex-1"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      Dettagli
                    </Button>
                    <Button
                      onClick={() => downloadReport(report.id)}
                      size="sm"
                      className="flex-1 bg-blue-600 hover:bg-blue-700"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      PDF
                    </Button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Report Details Dialog */}
        <Dialog open={showReportDetails} onOpenChange={setShowReportDetails}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Dettagli Report</DialogTitle>
            </DialogHeader>
            
            {selectedReport && (
              <div className="space-y-4 mt-4">
                <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <h3 className="font-bold text-lg mb-3">Metriche Finanziarie</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Ricavi Totali</p>
                      <p className="text-2xl font-bold text-green-700">
                        {formatCurrency(selectedReport.total_revenue)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Costi Totali</p>
                      <p className="text-2xl font-bold text-red-700">
                        {formatCurrency(selectedReport.total_costs)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Margine Netto</p>
                      <p className={`text-2xl font-bold ${selectedReport.total_margin >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(selectedReport.total_margin)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">EBITDA</p>
                      <p className={`text-2xl font-bold ${selectedReport.ebitda >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        {formatCurrency(selectedReport.ebitda)}
                      </p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-4 bg-gradient-to-r from-amber-50 to-orange-50">
                  <h3 className="font-bold text-lg mb-3">Break-Even Point</h3>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-gray-700">Fabbisogno Cassa Annuale:</span>
                      <span className="font-semibold text-orange-700">
                        {formatCurrency(selectedReport.cash_requirement_annual)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Mese Break-Even:</span>
                      <span className="font-semibold">
                        {selectedReport.break_even_point_month || 'Non raggiunto'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-700">Mesi al BEP:</span>
                      <span className="font-semibold">
                        {selectedReport.months_to_break_even ? selectedReport.months_to_break_even.toFixed(1) : 'N/A'}
                      </span>
                    </div>
                  </div>
                </Card>
                
                <Button
                  onClick={() => downloadReport(selectedReport.id)}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Scarica Report Completo PDF
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Reports;
